import SpecList from "@/components/site/SpecList";

/**
 * Single source of truth for the Privacy Policy and Terms of Service.
 *
 * Rendered in two places: the footer modals and the /privacy and /terms
 * routes. Keep the prose here so the two can never drift apart.
 *
 * Items are term/detail pairs rendered by SpecList as a hairline-separated
 * description list. They are deliberately not boxed — a box reads as a
 * discrete component, which made paragraphs of policy look like widgets.
 */

// Ordered as the site orders them: the two products with users first, then the
// rest grouped by family (-aria, then -Pulse).
export const APP_SCOPE = [
  {
    term: "Daubaria",
    detail: "A skill-based, non-gambling multiplayer card game.",
  },
  {
    term: "Vidaria",
    detail:
      "A social community and database to identify, share, and connect over plants.",
  },
  {
    term: "Peraria",
    detail: "A multi-asset tracking and portfolio intelligence platform.",
  },
  {
    term: "ScorePulse",
    detail:
      "A live event scoring application for real-time scores and standings.",
  },
  {
    term: "PixelPulse",
    detail:
      "A device-native media studio for capturing, editing, and publishing media.",
  },
  {
    term: "SievePulse",
    detail: "A data utility tracking valid road closures with zero noise.",
  },
  {
    term: "StrataPulse",
    detail:
      "A hazard telemetry utility that matches active Philippine hazards to your precise location.",
  },
  {
    term: "Agos",
    detail:
      "An Android keyboard for Roman typing and Traditional Baybayin, switchable from the keyboard itself.",
  },
  {
    term: "Pocket Peachy",
    detail:
      "A fictional chibi action-fighter for adults, with offline play and an Android release coming soon.",
  },
  {
    term: "Evi",
    detail:
      "An early Android prototype that records location observations and flags patterns for review on the phone. Not yet released.",
  },
];

// Each entry renders as one term with two descriptions: what is collected, and
// what it is used for. The sentences carry their own framing, so the old
// "Data Collected:" / "Purpose:" prefixes are gone.
export const DATA_USAGE = [
  {
    term: "Daubaria",
    detail: [
      "Collects account usernames, gameplay scores, and basic device information for matchmaking.",
      "Used to run multiplayer sessions, keep leaderboards fair, and maintain performance. No betting or gambling data is collected, because this is a strictly non-gambling platform.",
    ],
  },
  {
    term: "Vidaria",
    detail: [
      "Collects user profiles, uploaded plant photos, description text, and optional location metadata for identification.",
      "Used to enable community sharing, build the plant database, and help you connect with other growers.",
    ],
  },
  {
    term: "Peraria",
    detail: [
      "Collects the asset quantities, portfolio configurations, and tracking preferences you enter.",
      "Used to calculate portfolio intelligence and display tracking metrics, locally or securely on your account dashboard.",
    ],
  },
  {
    term: "ScorePulse",
    detail: [
      "Collects event and participant details, submitted scores, and the identity of the scorer or official recording them.",
      "Used to calculate live standings, keep every connected viewer in sync, and maintain an auditable record of score changes during an event.",
    ],
  },
  {
    term: "PixelPulse",
    detail: [
      "Collects the media files you import or capture, along with the edit settings and export preferences you apply to them.",
      "Used to render your edits and produce exports. Media is processed on your device wherever possible, and is not used to train models or shared for advertising.",
    ],
  },
  {
    term: "SievePulse",
    detail: [
      "Collects public or user-submitted road closure reports, and location data.",
      "Used to filter out irrelevant updates and display accurate, real-time road closure information.",
    ],
  },
  {
    term: "StrataPulse",
    // The badge carries the sensitivity signal now that the item is no longer
    // ringed by a coloured border. It is a word rather than a hue, so it
    // survives for screen readers and for anyone who cannot see the colour.
    badge: "Precise location",
    detail: [
      "Collects your device's precise location, including in the background if you turn on hazard alerts, together with the hazard types and alert thresholds you subscribe to.",
      "Used to work out which active hazards apply to where you actually are, and to warn you about those. Precise location is necessary because hazard boundaries are drawn tightly, and an alert for the wrong barangay is worse than no alert at all. Your location is used to match you against hazard zones and to deliver those warnings. It is not sold, not shared with advertisers, and not used to build an advertising profile. You can revoke location access at any time in your device settings; doing so disables location-based alerts but leaves the rest of the app usable.",
    ],
  },
  {
    term: "Agos",
    // Agos's own claim, stated in full and unaltered. The second sentence is
    // load-bearing and must not be dropped: a keyboard sees everything typed,
    // so an unqualified absolute would be the one claim here a reader is
    // entitled to test. This is also why the claim is not a card feature —
    // that field is line-clamp-2 and would truncate the qualifier away.
    detail: [
      "Collects your keyboard preferences, such as script, theme, and row spacing, along with your subscription status.",
      "Agos does not log, store, or upload what you type. The app receiving your text has its own data practices. Preferences are used to restore your keyboard set-up, and subscription status to confirm access.",
    ],
  },
  {
    term: "Pocket Peachy",
    detail: [
      "Stores game progress and results, tutorial choices, character preferences, disclaimer acceptance, and settings in the game's private storage on your device.",
      "The current offline game has no live accounts, ads, analytics, tracking, or active purchases, and it does not transmit this information to us or third parties. Clear the game's app storage or uninstall it to erase its local data.",
    ],
  },
  {
    term: "Evi",
    // Evi has no published privacy policy of its own — its drafts have no
    // public URL — so its handling is described here and nothing links out.
    // Every statement below is checked against the app's manifest and
    // documentation. Two rules from Evi's own release audit govern this entry:
    // no blanket "collects nothing" claim (local processing is still
    // processing), and the model is described as review assistance, never as
    // proof. Do not strengthen either.
    badge: "Precise location",
    detail: [
      "Collects your device's precise location, with timestamps, while a tracking session you have started is running. Opening the app never starts a session. Collection runs only as a foreground service with a visible notification and a Stop control, and continues until you stop it, even if you close the app. Evi does not request background location access. If you confirm a session start with your fingerprint, the fingerprint is checked by Android and never reaches Evi; Evi keeps only a signature showing the start was confirmed.",
      "Observations are stored in a private database on your phone and read there by a bundled model, which flags patterns for you to review. Evi does not declare the internet permission, has no accounts, and has no cloud sync, and its data is excluded from Android cloud backup and device-to-device transfer, so OneVidaria Digital never receives it. Evi does not currently let you edit, delete, or export individual observations. Uninstalling the app or clearing its storage removes them, and they do not move to a new phone.",
    ],
  },
];

// The term carries both the application and the clause topic, so the old
// duplicated "AppName (Category)" heading plus "Clause Topic:" lead-in
// collapses into one line.
export const PLATFORM_TERMS = [
  {
    term: "Daubaria — strictly non-gambling",
    detail:
      "Daubaria is purely a digital card-daubing game intended for entertainment purposes. No real money bets, wagering, digital tokens with cash value, or real-world prize payouts are involved. Any unauthorized attempt to use this platform for gambling activities will result in immediate account termination.",
  },
  {
    term: "Peraria — no financial advice",
    detail:
      "Peraria is an informational tracking and portfolio intelligence tool. OneVidaria Digital does not provide financial, investment, or legal advice. All data and calculations displayed are for informational purposes only. You are solely responsible for any financial actions or decisions you make.",
  },
  {
    term: "Vidaria — your content",
    detail:
      "You retain ownership of the photos and descriptions you upload. However, by posting content on Vidaria, you grant OneVidaria Digital a non-exclusive, worldwide, royalty-free license to display, host, and distribute that content to run the community platform.",
  },
  {
    term: "ScorePulse — record, not referee",
    detail:
      "ScorePulse records and displays scores entered by event organisers and officials. It does not adjudicate results. Where a displayed standing and an official ruling disagree, the official ruling governs.",
  },
  {
    term: "StrataPulse — not an official warning service",
    detail:
      "StrataPulse surfaces hazard information and matches it to your location. It is an informational aid and is not a substitute for PAGASA, PHIVOLCS, NDRRMC, or your local disaster risk reduction office. Alerts depend on upstream data feeds, device permissions, and network availability, any of which can fail or lag. Never rely on StrataPulse alone when deciding whether to evacuate; always follow instructions from official authorities.",
  },
  {
    term: "PixelPulse and SievePulse — operational dependency",
    detail:
      "These utilities analyze and display information based on automated logic and public or submitted data streams. While we strive for accuracy, we do not guarantee the absolute uptime, completeness, or error-free nature of automated processing or closure reports.",
  },
  {
    term: "Agos — what the keyboard sees",
    detail:
      "Agos does not log, store, or upload what you type. The app receiving your text has its own data practices, which are governed by that app's own policies rather than ours.",
  },
  {
    term: "Agos — subscription and trial",
    detail:
      "Agos is subscription-based and free for the first seven days, after which a subscription is required to keep typing. The subscription terms that actually bind, including billing, renewal, and cancellation, are published at agos.onevidariadigital.com/terms.html and govern in the event of any conflict with this page.",
  },
  {
    term: "Pocket Peachy — fictional parody and offline play",
    detail:
      "Pocket Peachy is a work of fictional parody and satire for adults aged 18 and over. Its characters and events are invented. The current game offers offline play with no active accounts, advertising, purchases, or online service.",
  },
  {
    // Same class of boundary as Peraria's "no financial advice" and
    // StrataPulse's "not an official warning service". Taken from Evi's own
    // stated limits; do not soften it into a capability claim.
    term: "Evi — flags, not findings",
    detail:
      "Evi's model was trained and evaluated on simulated data only and has not been validated on real-world data. A flag means a pattern is worth a person's review, nothing more, and fast legitimate travel can trigger one. A flag does not establish where someone was, who carried the phone, or whether any location was falsified, and must not be relied on as evidence of any of them. A fingerprint confirmation covers only the start of a session, not the locations recorded after it.",
  },
  {
    term: "Evi — prototype status",
    detail:
      "Evi is an early prototype, version 0.1.0, and has not been released. It has been tested on one phone; battery use, long-running reliability, and other Android versions have not been validated. It is not offered for download or installation.",
  },
];

/**
 * Terms governing paid client work.
 *
 * These exist because the Terms previously covered the applications only and no
 * commercial relationship at all — publishing prices without them is real
 * exposure. They cover the failure modes that actually occur in small studio
 * work: unpaid balances, stalled projects, scope creep, and third-party
 * outages the studio gets blamed for.
 *
 * Plain language on purpose. Not legal advice, and a sound starting position
 * rather than a finished contract — worth a lawyer's review once revenue
 * justifies it.
 */
export const CLIENT_SERVICE_TERMS = [
  {
    term: "Scope of engagement",
    detail:
      "What is included in a plan or project is what is written on our services page and in your quote. Anything outside that is quoted separately or billed at our published hourly rate. We will tell you before doing work that falls outside the agreed scope.",
  },
  {
    term: "Fees and currency",
    detail:
      "All fees are in US dollars. Monthly plans are billed in advance. Project work is billed 50% before work begins and 50% on delivery. Payment details are provided by email. We do not process payments through this website.",
  },
  {
    term: "Your responsibilities",
    detail:
      "You provide the content, images, credentials and platform access we need to do the work, and you confirm you have the right to use anything you send us. Delays in providing these will delay delivery.",
  },
  {
    term: "Content deadline on project work",
    detail:
      "If we have not received the content needed to complete a project within 30 days of the deposit, we may pause the project and re-quote it. Deposits already paid remain valid against the re-quoted work for six months.",
  },
  {
    term: "Revisions",
    detail:
      "Project fees include two rounds of revisions. Further changes are billed at our published hourly rate. A revision means changes to work already delivered — a change of direction or added scope is new work.",
  },
  {
    term: "Cancellation",
    detail:
      "Monthly plans run month to month. Either of us may cancel with 30 days' written notice. The current month is not refunded, and service continues to the end of the notice period.",
  },
  {
    term: "Refunds",
    detail:
      "Deposits and setup fees are non-refundable once work has begun, because the work has been done. If we cancel a project before starting, any deposit is returned in full.",
  },
  {
    term: "Third-party platforms and hosting",
    detail:
      "We are not responsible for outages, policy changes, price changes, account suspensions or data loss caused by hosting providers, social platforms, domain registrars or other third-party services. We will help you resolve issues where we can, but we do not control those services and cannot guarantee their behaviour.",
  },
  {
    term: "Ownership of work",
    detail:
      "On final payment, you own the website we deliver and the content you supplied. We retain ownership of our own reusable components, tooling and methods, and the right to use them again. Unless you ask us not to, we may show the finished work as an example of ours.",
  },
];

export const ACCEPTABLE_USE_ITEMS = [
  "Violate any local, national, or international laws or regulations.",
  "Upload, post, or share content that is abusive, harassing, defamatory, vulgar, or otherwise objectionable (especially on community-focused platforms like Vidaria).",
  "Attempt to disrupt, compromise, or bypass the security or proper working order of our software, servers, or networks.",
  "Use automated systems, bots, or scrapers to access our platforms without explicit authorization.",
  "Misrepresent your identity or falsely claim an affiliation with any person or entity.",
];

/** Sections are separated by a rule rather than enclosed in a panel. */
export function PolicySection({ title, children }) {
  return (
    <section className="border-t border-line pt-6">
      <h4 className="font-display text-xl font-normal tracking-tight text-white">
        {title}
      </h4>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-white/75">
        {children}
      </div>
    </section>
  );
}

export function PrivacyPolicyBody() {
  return (
    <>
      <p className="text-base leading-relaxed text-white/75">
        At OneVidaria Digital, we believe in clear, straightforward, and honest
        data handling. This Privacy Policy explains how we collect, use, and
        protect your information across our ten applications. We design our
        tools to give you a smooth experience without compromising your privacy.
      </p>

      <div className="mt-8 space-y-6">
        <PolicySection title="1. Scope of This Policy">
          <p>
            This policy applies exclusively to the following projects under
            OneVidaria Digital:
          </p>
          <SpecList items={APP_SCOPE} />
        </PolicySection>

        <PolicySection title="2. Information We Collect and How We Use It">
          <p>
            We only collect the minimum amount of data necessary to make each
            application function correctly and securely.
          </p>
          <SpecList items={DATA_USAGE} />
        </PolicySection>

        {/* Precise location, product by product. No sentence here may call
            one product "the only" or "the most" anything: that claim went
            false the moment a second product needed precise location, and it
            will go false again. Add a block for each new product that uses
            it. */}
        <PolicySection title="3. Precise Location">
          <p>
            StrataPulse and Evi use your device&apos;s precise location. They
            use it in very different ways, so each is set out here rather than
            buried in a list.
          </p>

          <h5 className="pt-2 text-sm font-medium text-white/90">
            StrataPulse
          </h5>
          <p>
            <span className="text-white/85">What it needs:</span> your device's
            precise location, and background location access if you choose to
            receive alerts while the app is closed.
          </p>
          <p>
            <span className="text-white/85">Why it needs it:</span> hazard
            boundaries are narrow. Matching you to the correct zone is the
            entire function of the product, and approximate location is not
            accurate enough to do it safely.
          </p>
          <p>
            <span className="text-white/85">What we do not do:</span> we do not
            sell your location, share it with advertisers, or use it to build a
            profile of you. It is used to match you to hazard zones and to send
            the alerts you asked for.
          </p>
          <p>
            <span className="text-white/85">Your control:</span> location access
            can be revoked at any time in your device settings. Location-based
            alerts stop working when you do; nothing else about the app breaks.
          </p>

          {/* Checked against Evi's manifest and source: user-started
              foreground service with a Stop action, stopWithTask=false, no
              ACCESS_BACKGROUND_LOCATION, no INTERNET, allowBackup=false with
              cloud backup and device transfer both excluded. */}
          <h5 className="pt-2 text-sm font-medium text-white/90">Evi</h5>
          <p>
            <span className="text-white/85">What it needs:</span> your device&apos;s
            precise location, only while a tracking session you have started
            is running. Opening the app never starts one. A session runs as a
            foreground service with a visible notification and a Stop control,
            and continues until you stop it, even if you close the app. Evi
            does not request background location access.
          </p>
          <p>
            <span className="text-white/85">Why it needs it:</span> recording
            where the phone was, exactly as captured, is what Evi is for.
          </p>
          <p>
            <span className="text-white/85">What we do not do:</span> we never
            receive it. Observations stay in a private database on your phone.
            Evi does not declare the internet permission, and its data is
            excluded from Android cloud backup and device-to-device transfer.
          </p>
          <p>
            <span className="text-white/85">Your control:</span> stop a session
            from its notification or from the app, or revoke location access
            in your device settings to stop Evi recording at all. Recorded
            observations are removed by uninstalling Evi or clearing its
            storage.
          </p>
        </PolicySection>

        <PolicySection title="4. Data Storage and Retention">
          <p>
            <span className="text-white/85">Local Processing:</span> Wherever
            possible, our utilities process data directly inside your browser or
            device to maximize speed and privacy.
          </p>
          <p>
            <span className="text-white/85">Retention Limits:</span> We keep
            personal data only for as long as it is actively needed to run the
            specific service you are using. When data is no longer necessary, it
            is securely deleted or anonymized.
          </p>
        </PolicySection>

        <PolicySection title="5. Information Sharing and Disclosure">
          <p>
            OneVidaria Digital does not sell, rent, or trade your personal
            information to third-party companies for marketing purposes. We only
            share data under the following conditions:
          </p>
          <p>
            <span className="text-white/85">Service Providers:</span> Trusted
            technical platforms that help us host our applications or send
            necessary account notifications.
          </p>
          {/* Every claim here is from Agos's backend/README.md and checked in
              backend/: no file or database writes in the runtime, telemetry
              limited to a whitelist with no token or address field, rate-limit
              counters and the rejected-token cache held in memory only. It
              also stays within Agos's own published privacy policy. Do not add
              a host or a log-retention period: neither is documented for the
              verification service. */}
          <p>
            <span className="text-white/85">Google Play, for Agos:</span> Agos
            subscriptions are billed through Google Play, which handles the
            payment under Google&apos;s own privacy policy. To confirm a
            subscription is valid, the Agos app sends its Google Play purchase
            token to our verification service, which checks it with Google Play
            and returns a signed confirmation to your device. The service also
            receives Google Play&apos;s notifications when the subscription&apos;s
            status changes. It does not store or log purchase tokens, and it
            accepts no typed text. Your network address is used briefly, in
            memory, to limit request rates and is not logged. The confirmation
            is kept on your device in encrypted form and lasts at most seven
            days, never beyond the subscription&apos;s expiry.
          </p>
          <p>
            <span className="text-white/85">Legal Compliance:</span> If required
            by law, regulation, or a formal legal request to protect safety and
            prevent fraud.
          </p>
        </PolicySection>

        <PolicySection title="6. Security Measures">
          <p>
            We use standard, industry-recognized security safeguards to shield
            your data from unauthorized access, loss, or alteration. This
            includes using secure connections (HTTPS) and scanning our
            development environments to ensure server keys and internal
            credentials are never exposed.
          </p>
        </PolicySection>

        <PolicySection title="7. Your Rights and Choices">
          <p>Depending on the app you use, you can:</p>
          <ul className="space-y-3">
            <li>
              Update or delete your account information directly inside the
              application settings.
            </li>
            <li>
              Control device permissions, such as camera access for Vidaria
              plant photos or location access for SievePulse, StrataPulse, and
              Evi.
            </li>
            <li>
              Contact us at any time to request a copy of the data we store or
              to ask for its complete removal.
            </li>
          </ul>
          {/* Mirrors the Evi entry in section 2. Worded as "does not apply to
              Evi" rather than "Evi is the only exception": other apps' storage
              is not verified here, and an exclusivity claim is exactly what
              went stale in section 3. */}
          <p>
            That last option does not apply to Evi. Its observations never
            reach us, so we hold nothing to copy or remove. They are stored
            only on your phone: uninstalling Evi or clearing its storage removes
            them, and Evi does not currently let you export, edit, or delete
            individual observations.
          </p>
        </PolicySection>
      </div>
    </>
  );
}

export function TermsOfServiceBody() {
  return (
    <>
      <p className="text-base leading-relaxed text-white/75">
        Welcome to OneVidaria Digital. Please read these Terms of Service
        ("Terms") carefully before using our applications. By accessing or using
        our services, you agree to be bound by these Terms. If you do not agree
        to these terms, you may not use our applications.
      </p>

      <div className="mt-8 space-y-6">
        <PolicySection title="1. Scope of Services">
          <p>
            These Terms cover two distinct things: your use of the applications
            developed and operated by OneVidaria Digital, and any paid client
            services you engage us for directly. Sections 1 to 4 concern the
            applications. Section 5 concerns client services, and applies only
            if you have engaged us for paid work.
          </p>
          <p>The applications covered are:</p>
          <SpecList items={APP_SCOPE} />
        </PolicySection>

        <PolicySection title="2. Eligibility and User Accounts">
          <p>
            <span className="text-white/85">Age Requirements:</span> You must
            meet the minimum legal age required in your country to use digital
            applications and services.
          </p>
          <p>
            <span className="text-white/85">Account Security:</span> If an
            application requires you to create an account, you are responsible
            for maintaining the confidentiality of your credentials and for all
            activities that occur under your account.
          </p>
          <p>
            <span className="text-white/85">Accurate Information:</span> You
            agree to provide accurate, current, and complete information when
            creating an account or interacting with our services.
          </p>
        </PolicySection>

        <PolicySection title="3. Acceptable Use Policy">
          <p>
            You agree that you will not use any OneVidaria Digital application
            to:
          </p>
          <ul className="space-y-3">
            {ACCEPTABLE_USE_ITEMS.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </PolicySection>

        <PolicySection title="4. Platform-Specific Terms">
          <SpecList items={PLATFORM_TERMS} />
        </PolicySection>

        <PolicySection title="5. Client Services">
          <p>
            This section applies to paid client work — website builds,
            maintenance plans, and social setup — engaged directly with
            OneVidaria Digital. Current scope and pricing are published on our
            services page.
          </p>
          <SpecList items={CLIENT_SERVICE_TERMS} />
        </PolicySection>

        <PolicySection title="6. Intellectual Property Rights">
          {/* Must not contradict Section 4: Vidaria's term leaves ownership of
              uploads with the user. Ownership here covers what we make; what
              users submit is governed by each product's own licence. */}
          <p>
            The software, code, designs, graphics, branding, and logos of our
            applications, and the original content we create for them, are the
            exclusive property of OneVidaria Digital. Content you submit, such
            as photos and descriptions you upload to Vidaria, remains yours,
            subject to the license set out for that application in Section 4.
          </p>
          <p>
            You are granted a limited, personal, non-transferable, and revocable
            license to access and use our apps for their intended purposes. You
            may not copy, modify, distribute, reverse engineer, or sell any part
            of our intellectual property.
          </p>
        </PolicySection>

        <PolicySection title="7. Limitation of Liability">
          <p>
            OneVidaria Digital provides its applications on an "as-is" and
            "as-available" basis. To the maximum extent permitted by law,
            OneVidaria Digital shall not be liable for any direct, indirect,
            incidental, special, or consequential damages resulting from your
            use of, or inability to use, our services, including but not limited
            to financial losses, data loss, or system downtime.
          </p>
        </PolicySection>

        <PolicySection title="8. Changes to the Terms">
          <p>
            We reserve the right to modify or update these Terms at any time. If
            we make material changes, we will update the "Last Updated" date at
            the top of this document or notify you directly through the affected
            application. Your continued use of our applications after changes
            are posted constitutes your acceptance of the updated Terms.
          </p>
        </PolicySection>

        <PolicySection title="9. Termination of Use">
          <p>
            We reserve the right to suspend or terminate your access to any or
            all OneVidaria Digital applications immediately, without prior
            notice, if you violate these Terms or engage in conduct that harms
            our platforms, users, or legal standing.
          </p>
        </PolicySection>

        <PolicySection title="10. Contact Us">
          <p>
            If you have any questions or concerns regarding these Terms of
            Service, please contact us through our official support channels.
          </p>
        </PolicySection>
      </div>
    </>
  );
}
